import React, { useState } from "react";
import { X, DollarSign, Calendar, TrendingUp } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const ConvertToOpportunityModal = ({ lead, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    opportunityName: `${lead.companyName || lead.fullName} - Opportunity`,
    description: `Converted from lead: ${lead.fullName || lead.email}`,
    amount: lead.expectedRevenue || "",
    currency: "GBP",
    probability: 50,
    expectedCloseDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    stage: "qualification",
    priority: lead.leadTemperature === "hot" ? "high" : "medium",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiService.convertLeadToOpportunity(
        lead.id,
        formData,
      );
      if (response.success) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error("Error converting to opportunity:", error);
      setError(
        error.response?.data?.message || "Failed to convert to opportunity",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getCurrencySymbol = (currency = "GBP") => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    return symbols[currency] || currency;
  };

  // Reusable theme classes
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
    isDarkMode
      ? "bg-gray-800 border-gray-700 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-2xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 border-b p-6 flex items-center justify-between ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-xl font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Convert to Opportunity
            </h2>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Create an opportunity from {lead.fullName || lead.companyName}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Lead Summary */}
          <div
            className={`rounded-lg p-4 border ${
              isDarkMode
                ? "bg-gray-800/50 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                >
                  Contact:
                </span>
                <div
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {lead.fullName}
                </div>
              </div>
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                >
                  Company:
                </span>
                <div
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {lead.companyName || "N/A"}
                </div>
              </div>
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                >
                  Email:
                </span>
                <div
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {lead.email || "N/A"}
                </div>
              </div>
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                >
                  Temperature:
                </span>
                <div
                  className={`font-medium ${
                    lead.leadTemperature === "hot"
                      ? "text-red-400"
                      : lead.leadTemperature === "warm"
                        ? "text-orange-400"
                        : "text-blue-400"
                  }`}
                >
                  {lead.leadTemperature || "cold"}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>Opportunity Name *</label>
              <input
                type="text"
                name="opportunityName"
                value={formData.opportunityName}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Expected Amount</label>
              <div className="relative">
                <DollarSign
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  className={`${inputClass} pl-10`}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Currency</label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="JPY">JPY (¥)</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>Probability (%)</label>
              <div className="relative">
                <TrendingUp
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="number"
                  name="probability"
                  value={formData.probability}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className={`${inputClass} pl-10`}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Expected Close Date</label>
              <div className="relative">
                <Calendar
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="date"
                  name="expectedCloseDate"
                  value={formData.expectedCloseDate}
                  onChange={handleChange}
                  className={`${inputClass} pl-10`}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Stage</label>
              <select
                name="stage"
                value={formData.stage}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="qualification">Qualification</option>
                <option value="needs_analysis">Needs Analysis</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div
            className={`flex items-center gap-3 pt-4 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-white rounded-lg font-medium transition-all duration-300 disabled:opacity-50"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              }}
            >
              {loading ? "Converting..." : "Convert to Opportunity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConvertToOpportunityModal;
